import { oauthPageFor, oauthRedirectTarget } from './oauth-flow.js';

/**
 * «Вернуться туда, откуда прислали на вход» — ровно для одной страницы:
 * закрытой документации MCP на admin-api (`/docs/mcp`, спека 2026-10-21
 * mcp-docs §4.3). admin-api без сессии отвечает 302 на
 * `${ADMIN_WEB_URL}/?returnTo=<origin admin-api>/docs/mcp`; после входа (или
 * сразу, если сессия уже есть) панель уводит браузер обратно.
 *
 * Это open-redirect-поверхность, поэтому allowlist строгий и проверяется
 * ДВАЖДЫ — при чтении из адреса и при выдаче из sessionStorage:
 *   - origin (схема + хост + порт) равен origin admin-api;
 *   - pathname — `/docs/mcp` или начинается с `/docs/mcp/`;
 *   - никаких userinfo (`@`), обратных слешей, пробелов/управляющих символов
 *     и закодированных `.`/`/`/`\` в пути.
 * Всё прочее молча игнорируется: вход идёт как обычно.
 */

export const RETURN_TO_PARAM = 'returnTo';
export const RETURN_TO_STORAGE_KEY = 'wit-admin:return-to';
/** Когда панель последний раз ушла по `returnTo` — защита от петли редиректов. */
export const RETURN_TO_FOLLOWED_KEY = 'wit-admin:return-to:followed-at';
/**
 * Сколько живёт отложенный возврат. Брошенный вход не должен через час
 * утащить оператора, пришедшего в ту же вкладку за инбоксом, на документацию.
 */
export const RETURN_TO_TTL_MS = 15 * 60_000;
/**
 * Окно, в котором второй уход по `returnTo` не делается. Если admin-api на
 * `/docs/mcp` не видит сессию, которую видит `/api/me` (cookie не дошёл,
 * разъехались `AUTH_BASE_URL`/`VITE_ADMIN_API_URL`, прокси), иначе вышла бы
 * бесконечная петля 302 → `/?returnTo` → `assign`.
 */
export const RETURN_TO_LOOP_GUARD_MS = 30_000;

const DOCS_PATH = '/docs/mcp';
// Whitespace, C0/C1 control chars and backslashes: the WHATWG URL parser
// silently strips or rewrites them (`\` → `/`, tabs/newlines dropped), which is
// exactly how «looks like our host» strings turn into somebody else's.
// eslint-disable-next-line no-control-regex
const FORBIDDEN_CHARS = /[\s\u0000-\u001f\u007f-\u009f\\]/;
// Percent-encoded dot, slash, backslash in the path: harmless to the URL
// parser, but a server that decodes before routing would see `..` / `/`.
const ENCODED_PATH_TRICKS = /%(2e|2f|5c)/i;

/**
 * Origin admin-api, против которого сверяется `returnTo`: origin
 * `VITE_ADMIN_API_URL`, а для относительного значения — origin самой страницы.
 */
export function apiOriginOf(apiUrl: string, pageOrigin: string): string {
  try {
    return new URL(apiUrl, pageOrigin).origin;
  } catch {
    return pageOrigin;
  }
}

/** Проверенный абсолютный адрес документации MCP или `null`. */
export function parseReturnTo(raw: string | null, apiOrigin: string): string | null {
  if (!raw || FORBIDDEN_CHARS.test(raw)) return null;
  // Only an absolute URL or a root-relative path (`/docs/mcp`). A scheme-
  // relative `//host` resolves to another origin below and is refused there;
  // a bare relative path (`docs/mcp`, `../x`) is refused outright.
  if (!/^[a-z][a-z0-9+.-]*:/i.test(raw) && !raw.startsWith('/')) return null;
  let url: URL;
  try {
    url = new URL(raw, apiOrigin);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  if (url.origin !== apiOrigin) return null;
  if (url.username || url.password) return null;
  if (ENCODED_PATH_TRICKS.test(url.pathname)) return null;
  if (url.pathname !== DOCS_PATH && !url.pathname.startsWith(`${DOCS_PATH}/`)) return null;
  return url.href;
}

/**
 * Запомнить до конца входа (переживает перезагрузку и внешний редирект во
 * вкладке) — вместе с моментом записи, см. {@link RETURN_TO_TTL_MS}.
 */
export function rememberReturnTo(url: string): void {
  try {
    window.sessionStorage.setItem(RETURN_TO_STORAGE_KEY, JSON.stringify({ url, at: Date.now() }));
  } catch {
    // Best-effort: без хранилища просто не вернёмся — вход не ломается.
  }
}

/**
 * Забрать запомненный адрес (одноразово). Просроченная, «из будущего» или
 * нечитаемая запись удаляется и даёт `null`.
 */
export function takeReturnTo(): string | null {
  try {
    const raw = window.sessionStorage.getItem(RETURN_TO_STORAGE_KEY);
    window.sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
    if (raw === null) return null;
    const v: unknown = JSON.parse(raw);
    if (!v || typeof v !== 'object') return null;
    const { url, at } = v as { url?: unknown; at?: unknown };
    if (typeof url !== 'string' || typeof at !== 'number') return null;
    const age = Date.now() - at;
    return age >= 0 && age < RETURN_TO_TTL_MS ? url : null;
  } catch {
    return null;
  }
}

/** Ушли по `returnTo` меньше {@link RETURN_TO_LOOP_GUARD_MS} назад? */
function followedRecently(): boolean {
  try {
    const at = Number(window.sessionStorage.getItem(RETURN_TO_FOLLOWED_KEY));
    const age = Date.now() - at;
    return Number.isFinite(at) && at > 0 && age >= 0 && age < RETURN_TO_LOOP_GUARD_MS;
  } catch {
    return false;
  }
}

function markFollowed(): void {
  try {
    window.sessionStorage.setItem(RETURN_TO_FOLLOWED_KEY, String(Date.now()));
  } catch {
    // Best-effort.
  }
}

/** Сбросить отложенный возврат (вход по MCP OAuth его вытесняет). */
export function forgetReturnTo(): void {
  try {
    window.sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
  } catch {
    // Best-effort.
  }
}

/**
 * Прочитать `?returnTo` из адреса страницы и запомнить, если он проходит
 * allowlist. Невалидный параметр игнорируется и не затирает уже запомненный.
 */
export function captureReturnTo(search: string, apiOrigin: string): void {
  const url = parseReturnTo(new URLSearchParams(search).get(RETURN_TO_PARAM), apiOrigin);
  if (url) rememberReturnTo(url);
}

/**
 * Один раз при загрузке SPA. На страницах MCP OAuth (`/oauth/login`,
 * `/oauth/consent`) их собственный вход главнее: отложенный возврат
 * сбрасывается, а `returnTo` из адреса не читается. На остальных страницах —
 * {@link captureReturnTo}.
 */
export function bootReturnTo(pathname: string, search: string, apiOrigin: string): void {
  if (oauthPageFor(pathname)) {
    forgetReturnTo();
    return;
  }
  captureReturnTo(search, apiOrigin);
  stripReturnToFromAddress(search);
}

/**
 * Убрать `returnTo` из адресной строки (валидный он или нет): иначе «Назад»
 * со страницы документации вернул бы на `/?returnTo=…`, и уже вошедшего
 * оператора снова унесло бы на документацию — ловушка кнопки «Назад».
 */
function stripReturnToFromAddress(search: string): void {
  const params = new URLSearchParams(search);
  if (!params.has(RETURN_TO_PARAM)) return;
  params.delete(RETURN_TO_PARAM);
  const rest = params.toString();
  try {
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`,
    );
  } catch {
    // Best-effort.
  }
}

/**
 * Запомненный возврат, ПОВТОРНО проверенный allowlist'ом, или `null`.
 * Забирает значение из хранилища в любом случае. Единственная точка, через
 * которую панель уходит по `returnTo`, поэтому здесь же защита от петли: в
 * окне {@link RETURN_TO_LOOP_GUARD_MS} после ухода — `null` (обычный вход).
 */
export function consumeReturnTo(
  apiOrigin: string,
  take: () => string | null = takeReturnTo,
): string | null {
  const url = parseReturnTo(take(), apiOrigin);
  if (!url || followedRecently()) return null;
  markFollowed();
  return url;
}

export type PostSignInAction =
  | { kind: 'assign'; url: string }
  | { kind: 'oauth-expired' }
  | { kind: 'app' };

/**
 * Что делать после успешного входа по паролю.
 *
 * Вход по MCP OAuth (`oauthQuery`) имеет приоритет и `returnTo` не трогает:
 * либо продолжение authorize-запроса, либо «запрос устарел». Обычный вход —
 * на проверенный `returnTo`, иначе в панель, как раньше.
 */
export function postSignInAction({
  oauthQuery,
  body,
  apiOrigin,
  take = takeReturnTo,
}: {
  oauthQuery?: string;
  body: unknown;
  apiOrigin: string;
  take?: () => string | null;
}): PostSignInAction {
  if (oauthQuery) {
    const target = oauthRedirectTarget(body);
    return target ? { kind: 'assign', url: target } : { kind: 'oauth-expired' };
  }
  const url = consumeReturnTo(apiOrigin, take);
  return url ? { kind: 'assign', url } : { kind: 'app' };
}
